import { CommonModule } from '@angular/common';
import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ConfigureInformationService } from '../configure-information.service';
import { finalize, Subject, take, takeUntil } from 'rxjs';
import { DynamicTableComponent } from 'app/shared/components/dynamic-table/dynamic-table.component';
import { DataTableActionEnum, DataTableColumnFiltersApi, DataTableColumnModel, DataTableStateModel } from 'app/shared/components/dynamic-table/dynamic-table.model';
import { ConfigurationService } from 'app/shared/services/configuration.service';
import { TitleHeaderComponent } from 'app/shared/components/title-header/title-header.component';
import { HelperService } from 'app/shared/services/helper.service';
import { ConfirmationDialogComponent } from 'app/shared/components/confirmation-dialog/confirmation-dialog.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ListResponse } from 'app/shared/models/list-response.model';
import { CONFIGURE_INFORMATION_TABLE_STATE } from './configure-information-datatable.config';
import { DiscardChangesService } from 'app/shared/services/discard-changes.service';
import { UsersService } from 'app/shared/services/users.service';

@Component({
  selector: 'app-configure-information-datatable',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    DynamicTableComponent,
    TitleHeaderComponent
  ],
  templateUrl: './configure-information-datatable.component.html',
  styleUrl: './configure-information-datatable.component.scss',
})
export class ConfigureInformationDatatableComponent implements OnInit, OnDestroy {
  sidebarItemData: any = {};
  tableData: any = [];
  orgTableData: any = [];
  columns: DataTableColumnModel[] = [];
  loading: boolean = false;
  configDataTableState: DataTableStateModel = {} as DataTableStateModel;
  orgConfigDataTableState: DataTableStateModel = {} as DataTableStateModel;
  totalCount: number = 0;
  filters: DataTableColumnFiltersApi[] = [];

  private unsubscribe$ = new Subject<void>();

  constructor(
    private configureInformationService: ConfigureInformationService,
    private configurationService: ConfigurationService,
    private helperService: HelperService,
    private modalService: NgbModal,
    private discardChangesService: DiscardChangesService,
    private translate: TranslateService,
    private usersService: UsersService
  ) { }

  ngOnInit() {
    this.getSidebarData();
  }

  getSidebarData() {
    this.configDataTableState = {} as DataTableStateModel;
    this.orgConfigDataTableState = {} as DataTableStateModel;
    this.sidebarItemData = {};

    this.configureInformationService.sidebarItemDataEmiter
      .pipe(
        takeUntil(this.unsubscribe$)
      )
      .subscribe((item: any) => {
        this.configDataTableState = { ...CONFIGURE_INFORMATION_TABLE_STATE };
        this.orgConfigDataTableState = { ...CONFIGURE_INFORMATION_TABLE_STATE };
        this.sidebarItemData = this.helperService.deepCopy(item);
        this.getTableData();
        window.scroll({
          top: 0,
          left: 0,
          behavior: 'smooth'
        });
      });
  }

  getTableData() {
    this.columns = [];
    this.tableData = [];
    this.orgTableData = [];
    this.filters = [];
    this.getTableList();
  }

  getTableList() {
    this.loading = false;
    this.columns = this.sidebarItemData.columns;

    this.configureInformationService.getConfigureInformationList(this.sidebarItemData.type, this.configDataTableState)
      .pipe(
        finalize(() => {
          this.loading = true;
        }),
        take(1)
      )
      .subscribe((res: ListResponse<any>) => {
        this.tableData = res.items;
        this.totalCount = res.totalCount;
        this.orgConfigDataTableState = this.helperService.deepCopy(this.configDataTableState);
        this.orgTableData = this.helperService.deepCopy(this.tableData);
      });
  }

  getTotalCount() {
    this.configureInformationService.getConfigureInformationList(this.sidebarItemData.type, this.configDataTableState)
      .pipe(take(1))
      .subscribe((res: ListResponse<any>) => {
        this.totalCount = res.totalCount;
      });
  }

  getDataTableState(data: DataTableStateModel) {
    this.configDataTableState = data;
    this.refreshData();
  }

  getCurrentEditedRow(data: any) {
    if(this.sidebarItemData.hasUsersSelect) {
      this.getUserRelatedSelectItems(data);

      if(this.sidebarItemData.hasTwoEntries && data.columnProperty === 'entry1') {
        this.getRelatedSelectItems(data);
      }

      if(this.sidebarItemData.hasThreeEntries && (data.columnProperty === 'entry1' || data.columnProperty === 'entry2')) {
        this.getRelatedSelectItems(data);
      }

    } else {
      this.getRelatedSelectItems(data);
    }

    setTimeout(() => {
      this.validateRow(data);
    });
  }

  changeInputField(data: any) {
    data.row.hasChanges = true;
    this.discardChangesService.setUnsavedChangesState(true);

    if(this.sidebarItemData.hasUsersSelect && data.columnProperty === 'comments') {
      return;
    }

    data.row.saveEnabled = !!data.event?.target?.value;
  }

  enableEdit(data: any) {
    if (!this.sidebarItemData.isSimple) {
      this.getSelectItems(data);
    }
  }

  getUsers(searchParam: string, data: any, initialLoading?: boolean) {
    this.usersService.getUsers(searchParam, 0, 50)
    .pipe(take(1))
    .subscribe((res: any) => {
      this.setRelatedUserItems(data, res, initialLoading);
    });
  }

  validateRow(data: any) {
    const prefix = data.columnProperty.replace(/[0-9]/g, '');
    const limit = +this.sidebarItemData.numberOfColumns || 6;

    for (let i = 1; i <= limit; i++) {
      const entry = `${prefix + (parseInt(i.toString()))}`;
      const skipValidation = this.sidebarItemData.skipValidation && this.sidebarItemData.skipValidation.includes(entry);

      if(this.sidebarItemData.hasUsersSelect) {
        if (!data.row['entry1'] && !skipValidation) {
          data.row.saveEnabled = false;
          return;
        }
      } else {
        if (!data.row[entry] && !skipValidation) {
          data.row.saveEnabled = false;
          return;
        }
      }
    }

    data.row.saveEnabled = true
  }

  getSelectItems(data: any) {
    if(data.columnProperty === 'comments') {
      return;
    }
    const params = {
      type: this.sidebarItemData.type,
      entry1: data.row.entry1 || '',
      entry2: data.row.entry2 || '',
      entry3: data.row.entry3 || '',
      entry4: data.row.entry4 || '',
      entry5: data.row.entry5 || '',
      entry6: data.row.entry6 || ''
    }

    const initialLoading = data.columnProperty + '_InitialLoading';

    if (data.row[initialLoading]) {
      return;
    }

    if(this.sidebarItemData.hasUsersSelect && (data.columnProperty === 'user1' || data.columnProperty === 'user2')) {
      data.row[initialLoading] = true;
      this.getUsers('', data, data.row[initialLoading]);
    } else {
      this.configurationService.getDropdownConfigurations(params, data.columnProperty)
        .pipe(take(1))
        .subscribe((res: any) => {
          data.row[initialLoading] = true;
          this.setRelatedAndAddedItems(data, res, data.row[initialLoading]);
        })
    }

  }

  getRelatedSelectItems(data: any) {
    const prefix = data.columnProperty.replace(/[0-9]/g, '');
    const numberPart = data.columnProperty.replace(/[^\d]/g, '');
    const nextColumnProperty = prefix + (parseInt(numberPart) + 1);

    let startNumber = +numberPart + 1;
    const limit = +this.sidebarItemData.numberOfColumns || 6;

    for (startNumber; startNumber <= limit; startNumber++) {
      const relatedItems = `${prefix + (parseInt(startNumber.toString()))}_items`;
      const addedRelatedItems = `${prefix + (parseInt(startNumber.toString()))}_added_items`;
      const entry = `${prefix + (parseInt(startNumber.toString()))}`;

      if (data.row[addedRelatedItems]) {
        data.row[relatedItems] = [];
      }
      data.row[relatedItems] = [];
      data.row[entry] = '';
      data.row.hasChanges = true;

      this.discardChangesService.setUnsavedChangesState(true);
    }

    if (!data.row[data.columnProperty] || (data.columnProperty === this.sidebarItemData.lastColumn)) {
      return;
    }

    for (let i = 1; i < (+numberPart + 1); i++) {
      const entry = `${prefix + (parseInt(i.toString()))}`;

      if (!data.row[entry]) {
        return;
      }
    }

    const params = {
      type: this.sidebarItemData.type,
      entry1: data.row.entry1 || '',
      entry2: data.row.entry2 || '',
      entry3: data.row.entry3 || '',
      entry4: data.row.entry4 || '',
      entry5: data.row.entry5 || '',
      entry6: data.row.entry6 || ''
    }

    this.configurationService.getDropdownConfigurations(params, nextColumnProperty)
      .pipe(take(1))
      .subscribe((res: any) => {
        const relatedItems = `${nextColumnProperty}_items`;
        const addedRelatedItems = `${nextColumnProperty}_added_items`;

        data.row[addedRelatedItems] = [];
        data.row[relatedItems] = res;
      });

  }

  getUserRelatedSelectItems(data: any) {
    if(data.columnProperty === 'entry1' || data.columnProperty === 'entry2' || data.columnProperty === 'entry3') {
      this.resetUserFields(data);
    }

    data.row.hasChanges = true;
    this.discardChangesService.setUnsavedChangesState(true);
  }

  addNewRow() {
    let newRow = {};
    newRow = this.helperService.deepCopy(this.sidebarItemData.addNewRow);

    if (newRow) {
      this.tableData.unshift(newRow);
      this.tableData = [...this.tableData];
    }
  }

  actionButton(data: any) {
    if (data.action === DataTableActionEnum.DELETE) {
      const modalRef = this.modalService.open(ConfirmationDialogComponent, {
        backdrop: 'static',
        keyboard: false,
        windowClass: 'confirmation-modal'
      });

      const titleText = data.row.name ? data.row.name : 'label.new_item';
      modalRef.componentInstance.title = `${titleText}`;
      modalRef.componentInstance.message = 'messages.are_you_sure_you_want_to_delete_this_item';
      modalRef.componentInstance.notConfirmActionLabel = 'label.cancel';
      modalRef.componentInstance.confirmActionLabel = 'label.confirm';

      modalRef.result.then(
        (result) => {
          if (result) {
            if (data.row.id) {
              this.deleteConfigureInformation(data.row, data.rowIndex);
            } else {
              this.tableData.splice(data.rowIndex, 1);
              this.tableData = [...this.tableData];
            }
          }
        }
      );
    } else if (data.action === DataTableActionEnum.DISCARD_CHANGES) {
      this.openConfirmationModal(true, data.row, data.rowIndex);
    } else {
      let params = {};
      if(this.sidebarItemData.hasUsersSelect) {
        params = {
          type: this.sidebarItemData.type,
          id: data.row.id || null,
          entry1: data.row.entry1,
          entry2: data.row.entry2 || null,
          entry3: data.row.entry3 || null,
          user1Id: data.row.user1_selectedItem && data.row.user1_selectedItem.id ? data.row.user1_selectedItem.id : (data.row.user1Id || null),
          user2Id: data.row.user2_selectedItem && data.row.user2_selectedItem.id ? data.row.user2_selectedItem.id : (data.row.user2Id || null),
          comments: data.row.comments || null,
        }
      } else {
        params = {
          type: this.sidebarItemData.type,
          id: data.row.id || null,
          entry1: data.row.entry1,
          entry2: data.row.entry2 || null,
          entry3: data.row.entry3 || null,
          entry4: data.row.entry4 || null,
          entry5: data.row.entry5 || null,
          entry6: data.row.entry6 || null,
        }
      }
      this.saveConfigureInformation(params, data.row);
    }
  }

  saveConfigureInformation(params: any, row: any) {
    this.configureInformationService.saveConfigureInformation(params)
      .pipe(take(1))
      .subscribe((res: number) => {
        row.saveEnabled = false;
        row.hasChanges = false;

        if (!params.id) {
          row.id = res;
        }

        this.orgTableData = this.helperService.deepCopy(this.tableData);
        this.getTotalCount();
        this.checkHasChanges();
      });
  }

  deleteConfigureInformation(row: any, rowIndex: number) {
    let deleteId = row.id;

    this.configureInformationService.deleteConfigureInformation(deleteId)
      .pipe(take(1))
      .subscribe(() => {
        this.tableData.splice(rowIndex, 1);
        this.tableData = [...this.tableData];
        this.getTotalCount();
      })
  }

  onSelectSearch(data: any) {
    const params = {
      type: this.sidebarItemData.type,
      id: data.row.id || null,
      entry1: data.columnProperty === 'entry1' ? data.search : (data.row.entry1 || null),
      entry2: data.columnProperty === 'entry2' ? data.search : (data.row.entry2 || null),
      entry3: data.columnProperty === 'entry3' ? data.search : (data.row.entry3 || null),
      entry4: data.columnProperty === 'entry4' ? data.search : (data.row.entry4 || null),
      entry5: data.columnProperty === 'entry5' ? data.search : (data.row.entry5 || null),
      entry6: data.columnProperty === 'entry6' ? data.search : (data.row.entry6 || null),
    }

    if(this.sidebarItemData.hasUsersSelect && (data.columnProperty === 'user1' || data.columnProperty === 'user2')) {
      this.getUsers(data.search, data);
    } else {
      this.configurationService.getDropdownConfigurations(params, data.columnProperty)
        .pipe(take(1))
        .subscribe((res: any) => {
          this.setRelatedAndAddedItems(data, res);
        })
    }
  }

  onClear(data: any) {
    const params = {
      type: this.sidebarItemData.type,
      entry1: data.row.entry1 || '',
      entry2: data.row.entry2 || '',
      entry3: data.row.entry3 || '',
      entry4: data.row.entry4 || '',
      entry5: data.row.entry5 || '',
      entry6: data.row.entry6 || ''
    }

    if(this.sidebarItemData.hasUsersSelect && (data.columnProperty === 'user1' || data.columnProperty === 'user2')) {
      
      const prop = data.columnProperty;
      data.row[prop] = '';
      data.row[`${prop}Id`] = null;
      data.row[`${prop}_selectedItem`] = {};

      this.getUsers('', data);
    } else {
      this.configurationService.getDropdownConfigurations(params, data.columnProperty)
        .pipe(take(1))
        .subscribe((res: any) => {
          this.setRelatedAndAddedItems(data, res);
        });
    }

  }

  setRelatedAndAddedItems(data: any, res: any, initialLoading?: boolean) {
    if (!initialLoading) {
      data.row.hasChanges = true;
      this.discardChangesService.setUnsavedChangesState(true);
    }

    const relatedItems = `${data.columnProperty}_items`;
    const addedRelatedItems = `${data.columnProperty}_added_items`;

    if (data.row[addedRelatedItems]) {
      data.row[relatedItems] = [...res, ...data.row[addedRelatedItems]];
    } else {
      data.row[relatedItems] = res;
    }
  }

  setRelatedUserItems(data: any, res: any, initialLoading?: boolean) {
    if (!initialLoading) {
      data.row.hasChanges = true;
      this.discardChangesService.setUnsavedChangesState(true);
    }
    const relatedItems = `${data.columnProperty}_items`;
    data.row[relatedItems] = res;
  }

  resetUserFields(data: any) {
    data.row['user1'] = '';
    data.row['user1Id'] = null;
    data.row['user1_selectedItem'] = {};
    
    if(data.row['user2']) {
      data.row['user2'] = '';
      data.row['user2Id'] = null;
      data.row['user2_selectedItem'] = {};
    }

    if(data.row['comments']) {
      data.row['comments'] = '';
    }

    if(data.row['entry2'] && data.columnProperty === 'entry1') {
      data.row['entry2'] = '';
    }

    if(data.row['entry3'] && (data.columnProperty === 'entry1' || data.columnProperty === 'entry2')) {
      data.row['entry3'] = '';
    }
  }

  refreshData() {
    let hasChanges = false;

    for (let i = 0; i < this.tableData.length; i++) {

      if (this.tableData[i]?.hasChanges) {
        hasChanges = true;
        break;
      }
    }

    if (hasChanges) {
      this.openConfirmationModal();
    } else {
      this.getTableList();
    }
  }

  openConfirmationModal(isDiscardChanges?: boolean, data?: any, rowIndex?: number) {
    const modalRef = this.modalService.open(ConfirmationDialogComponent, {
      backdrop: 'static',
      keyboard: false,
      windowClass: 'confirmation-modal'
    });

    modalRef.componentInstance.title = `messages.${isDiscardChanges ? 'discard_changes' : 'unsaved_changes_message'}`;
    modalRef.componentInstance.message = `messages.${isDiscardChanges ? 'are_you_sure_discard_changes' : 'you_will_lose_changes_message'}`;
    modalRef.componentInstance.notConfirmActionLabel = 'buttons.cancel';
    modalRef.componentInstance.confirmActionLabel = 'buttons.confirm';

    modalRef.result.then(
      (result) => {
        if (result) {
          if (isDiscardChanges) {
            this.discardChangesService.setUnsavedChangesState(false);
            this.handleDiscardRowChanges(data, rowIndex);
          } else {
            this.getTableList();
          }
        } else {
          this.configDataTableState = this.helperService.deepCopy(this.orgConfigDataTableState);
        }
      }
    );
  }

  handleDiscardRowChanges(data?: any, rowIndex?: number) {

    if (!data?.id) {
      let newRow = {};
      newRow = this.helperService.deepCopy(this.sidebarItemData.addNewRow);
      data = newRow;
      this.tableData[rowIndex || 0] = newRow;
      this.tableData = [...this.tableData];
      return;
    }

    for (let orgRow of this.orgTableData) {
      if (orgRow.id === data.id) {
        this.discardRowChanges(orgRow);
        break
      }
    }
  }

  discardRowChanges(orgRow: any) {
    for (let i = 0; i < this.tableData.length; i++) {
      if (this.tableData[i]?.id === orgRow.id) {
        this.tableData[i] = { ...orgRow };
        break
      }
    }
    this.tableData = [...this.tableData];
  }

  checkHasChanges() {
    let hasChanges = false;

    for (let i = 0; i < this.tableData.length; i++) {

      if (this.tableData[i]?.hasChanges) {
        hasChanges = true;
        break;
      }
    }

    this.discardChangesService.setUnsavedChangesState(hasChanges);
  }

  @HostListener('window:beforeunload', ['$event'])
  unloadNotification(event: BeforeUnloadEvent): void {
    const hasChanges = this.discardChangesService.getUnsavedChangesState();
    if (hasChanges) {
      event.preventDefault();
      event.returnValue = this.translate.instant('messages.unsaved_changes_message');
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

}
